import { uid, isDefined } from "@dhx/ts-common/core";
import { IEventSystem } from "@dhx/ts-common/events";
import {
	DataCallback,
	DataDriver,
	DataEvents,
	dhxError,
	IDataItem,
	toDataDriver,
	TreeCollection,
} from "@dhx/ts-data";
import { TreeGridEvents } from "./types";
import { Id } from "@dhx/ts-common/types";

export class TreeGridCollection extends TreeCollection {
	constructor(config?: any, events?: IEventSystem<DataEvents | TreeGridEvents>) {
		super(config, events);
	}
	eachChild(id: Id, cb: any, direct = true, checkItem?: (item: IDataItem) => boolean) {
		checkItem = checkItem || (item => item.$opened !== false);
		super.eachChild(id, cb, direct, checkItem);
	}

	getMaxLevel(): number {
		let maxLevel = 1;
		this.map(el => {
			const level = this.getLevel(el.id);
			maxLevel = Math.max(level, maxLevel);
		});
		return maxLevel;
	}

	getLevel(id: Id): number {
		let level = 0;
		this.eachParent(id, () => {
			level++;
		});
		return level;
	}
	serialize(driver: DataDriver = DataDriver.json): any[] {
		const data = [];
		super.eachChild(this.getRoot(), el => {
			if (!el) {
				return;
			}
			const cell = { ...el };
			Object.keys(cell).forEach(key => {
				if (key.startsWith("$") && key !== "$groupName" && key !== "$level") {
					delete cell[key];
				}
			});
			data.push(cell);
		});
		const dataDriver = toDataDriver(driver);
		if (dataDriver) {
			return dataDriver.serialize(data);
		}
	}

	getPlainIndex(id: Id): number {
		return Object.keys(this._pull).indexOf(`${id}`);
	}
	map(cb: DataCallback<IDataItem>, parent: Id = this._root, direct = true): IDataItem[] {
		let result: any[] = [];
		if (!this.haveItems(parent)) {
			return result;
		}
		for (let i = 0; i < this._childs[parent].length; i++) {
			result.push(cb.call(this, this._childs[parent][i], i));
			if (direct && this._childs[parent][i].hasOwnProperty("$opened")) {
				const childResult = this.map(cb, this._childs[parent][i].id, direct);
				result = result.concat(childResult);
			}
		}
		return result;
	}
	mapVisible(cb: DataCallback<IDataItem>, parent: Id = this._root, direct = true): IDataItem[] {
		let result: any[] = [];
		if (!this.haveItems(parent)) {
			return result;
		}
		for (let i = 0; i < this._childs[parent].length; i++) {
			if (!this._childs[parent][i].hidden) {
				result.push(cb.call(this, this._childs[parent][i], i));
				if (direct && this._childs[parent][i].$opened) {
					const childResult = this.mapVisible(cb, this._childs[parent][i].id, direct);
					result = result.concat(childResult);
				}
			}
		}
		return result;
	}
	getId(index: number) {
		return Object.keys(this._pull)[index];
	}
	protected _parse_data(data: any, parent = this._root) {
		let index = this._order.length;
		for (let obj of data) {
			if (this.config.init) {
				obj = this.config.init(obj);
			}
			for (const key of Object.keys(obj)) {
				key.includes("$items") && delete obj[key];
			}
			obj.id = obj.id ?? uid();
			obj.parent =
				typeof obj.parent === "undefined" || (obj.parent && obj.$items) ? parent : obj.parent;

			if (this._pull[obj.id]) {
				dhxError(`Item ${obj.id} already exist`);
			}

			this._pull[obj.id] = obj;
			this._order[index++] = obj;

			if (!this._childs[obj.parent]) {
				this._childs[obj.parent] = [];
			}
			this._childs[obj.parent].push(obj);

			if (obj.items && obj.items instanceof Object) {
				obj.$opened = true;
				this._parse_data(obj.items, obj.id);
			}
		}
		this.eachChild(
			this._root,
			item => (item.$level = this.getLevel(item.id)),
			true,
			() => true
		);
		this._checkItems();
	}
	protected _copy(
		id: Id,
		index: number,
		target: TreeGridCollection = this,
		targetId: Id = this._root,
		key?: number
	): Id {
		if (!this.exists(id)) {
			return null;
		}
		const currentChilds = this._childs[id];
		if (key) {
			index = index === -1 ? -1 : index + key;
		}
		if (target === this && !this.canCopy(id, targetId)) {
			return null;
		}
		const item = { ...this.getItem(id) };
		if (target.exists(id)) {
			item.id = uid();
		}
		if (this.exists(id)) {
			item.parent = targetId;
			if (target !== this && targetId === this._root) {
				item.parent = target.getRoot();
			}
			target.add(item, index);
			id = item.id;
		}
		if (currentChilds) {
			for (const child of currentChilds) {
				const childId = child.id;
				const childIndex = this.getIndex(childId);
				if (typeof id === "string") {
					this.copy(childId, childIndex, target, id);
				}
			}
		}

		return id;
	}
	protected _addToOrder(_order, obj: any, index: number): void {
		super._addToOrder(_order, obj, index);

		obj.$level = this.getLevel(obj.id);
		this._checkItems();
	}
	protected _removeCore(id: Id) {
		super._removeCore(id);
		this._checkItems();
	}
	private _checkItems() {
		this.eachChild(
			this._root,
			item => {
				const haveItems = this.haveItems(item.id);
				item.$items = haveItems;
				item.$opened = isDefined(item.$opened) ? item.$opened : haveItems;
			},
			true,
			() => true
		);
	}
}
